import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ControlNumberField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { DeviationEnum } from 'gql/graphql';
import type { BimWorkOrderPriceMtrDistributionInput } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { cnMixSpace, IconAlert, Informer, Text } from 'uikit';
import { formatNumberToRu } from 'utils';
import { replacerCost } from 'utils/replacerCost';

import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { BimWorkOrderPriceMtrDistEditConfirmationDialog } from './BimWorkOrderPriceMtrDistEditConfirmationDialog';
import { useBimUpdateWorkOrderPriceMTRDistribution } from './hooks/useBimUpdateWorkOrderPriceMTRDistribution';
import { useBimWorkOrderPriceMTRDistribution } from './hooks/useBimWorkOrderPriceMTRDistribution';
import { useBimWorkOrderPriceMTRDistributionTotal } from './hooks/useBimWorkOrderPriceMTRDistributionTotal';

interface IBimWorkOrderPriceMtrDistEdit {
    workOrderPriceId: number;
    onClose: (isSuccess: boolean) => void;
}

const styleWidth = { width: '100%' };
const headerStyle = {
    display: 'flex',
    paddingBottom: 8,
    marginBottom: 8,
    borderBottom: '1px solid #00416633',
};
const headerStyleLabel = { minWidth: 200, marginRight: 12 };

export const BimWorkOrderPriceMtrDistEdit: React.FC<IBimWorkOrderPriceMtrDistEdit> = (props) => {
    const { workOrderPriceId, onClose } = props;

    const [isDeviated, setIsDeviated] = useState(false);
    const [isDeviationChecked, setIsDeviationChecked] = useState(false);
    const [total, setTotal] = useState<number>();
    const [formInput, setFormInput] = useState<BimWorkOrderPriceMtrDistributionInput>();

    const { bimWorkOrderPriceMTRDistributionData, bimWorkOrderPriceMTRDistributionReset } =
        useBimWorkOrderPriceMTRDistribution(workOrderPriceId);
    const {
        bimWorkOrderPriceMTRDistributionTotalReset,
        bimWorkOrderPriceMTRDistributionTotalData,
        bimWorkOrderPriceMTRDistributionTotalStatus,
        bimWorkOrderPriceMTRDistributionTotalMakeRequest,
    } = useBimWorkOrderPriceMTRDistributionTotal(workOrderPriceId);

    const onSuccess = useCallback(() => {
        bimWorkOrderPriceMTRDistributionTotalReset(undefined);
        bimWorkOrderPriceMTRDistributionReset(undefined);

        onClose(true);
    }, [bimWorkOrderPriceMTRDistributionReset, bimWorkOrderPriceMTRDistributionTotalReset, onClose]);

    const {
        bimUpdateWorkOrderPriceMTRDistributionStatus,
        bimUpdateWorkOrderPriceMTRDistributionRequest,
        bimUpdateWorkOrderPriceMTRDistributionMakeRequest,
        bimUpdateWorkOrderPriceMTRDistributionReset,
    } = useBimUpdateWorkOrderPriceMTRDistribution(workOrderPriceId, onSuccess);

    const { control, formState, handleSubmit, setValue } = useForm();
    const mpValue = useWatch({ control, name: 'mp' });
    const mzValue = useWatch({ control, name: 'mz' });
    const opValue = useWatch({ control, name: 'op' });
    const ozValue = useWatch({ control, name: 'oz' });

    const isCommit = useMemo(
        () =>
            bimWorkOrderPriceMTRDistributionTotalStatus === RequestStatus.FETCHING ||
            bimUpdateWorkOrderPriceMTRDistributionStatus === RequestStatus.FETCHING,
        [bimUpdateWorkOrderPriceMTRDistributionStatus, bimWorkOrderPriceMTRDistributionTotalStatus],
    );

    const onCloseLocal = useCallback(() => {
        bimWorkOrderPriceMTRDistributionTotalReset(undefined);
        bimUpdateWorkOrderPriceMTRDistributionReset();
        onClose(false);
    }, [bimUpdateWorkOrderPriceMTRDistributionReset, bimWorkOrderPriceMTRDistributionTotalReset, onClose]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onCloseLocal,
        });

    const updateWorkOrderPriceMTRVariables = useMemo(() => {
        if (formInput) {
            return {
                workOrderPriceId,
                input: formInput,
            };
        }

        return null;
    }, [formInput, workOrderPriceId]);

    const {
        isOpenConfirmationDialog: isOpenDeviatedConfirmationDialog,
        handleOpenConfirmationDialog: handleOpenDeviatedConfirmationDialog,
        handleCloseConfirmationDialog: handleCloseDeviatedConfirmationDialog,
        handleActionQuery: handleActionDeviatedConfirmationQuery,
    } = useMethodsConfirmationDialog({
        actionQuery: bimUpdateWorkOrderPriceMTRDistributionRequest,
        variables: updateWorkOrderPriceMTRVariables,
        onBeforeActionQuery: bimUpdateWorkOrderPriceMTRDistributionReset,
    });

    const onModalClose = useCallback(() => {
        if (formState?.isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onCloseLocal();
        }
    }, [formState?.isDirty, handleOpenConfirmationDialog, onCloseLocal]);

    const onDeviatedConfirmationClose = useCallback(
        (value: boolean) => {
            if (!value) {
                setIsDeviated(true);
            }

            handleCloseDeviatedConfirmationDialog();
        },
        [handleCloseDeviatedConfirmationDialog],
    );

    const onSubmitDialog = useCallback(
        (formData) => {
            const input = {
                mp: replacerCost(formData.mp),
                mz: replacerCost(formData.mz),
                op: replacerCost(formData.op),
                oz: replacerCost(formData.oz),
            };

            setFormInput(input);

            if (isDeviationChecked) {
                bimUpdateWorkOrderPriceMTRDistributionMakeRequest(input);
            } else {
                bimWorkOrderPriceMTRDistributionTotalMakeRequest(input);
            }
        },
        [
            bimUpdateWorkOrderPriceMTRDistributionMakeRequest,
            bimWorkOrderPriceMTRDistributionTotalMakeRequest,
            isDeviationChecked,
        ],
    );

    useEffect(() => {
        if (bimWorkOrderPriceMTRDistributionData?.bimWorkOrderPriceMTRDistribution?.data) {
            const {
                mp,
                mz,
                op,
                oz,
                total: t,
            } = bimWorkOrderPriceMTRDistributionData?.bimWorkOrderPriceMTRDistribution?.data || {};

            setValue('mp', mp);
            setValue('mz', mz);
            setValue('op', op);
            setValue('oz', oz);
            setTotal(t);
        }
    }, [bimWorkOrderPriceMTRDistributionData?.bimWorkOrderPriceMTRDistribution?.data, setValue]);

    useEffect(() => {
        if (
            [DeviationEnum.Up, DeviationEnum.Down].includes(
                bimWorkOrderPriceMTRDistributionTotalData?.bimWorkOrderPriceMTRDistributionTotal?.data
                    ?.deviation as DeviationEnum,
            )
        ) {
            handleOpenDeviatedConfirmationDialog();
        } else if (
            bimWorkOrderPriceMTRDistributionTotalData?.bimWorkOrderPriceMTRDistributionTotal?.data?.deviation ===
            DeviationEnum.None
        ) {
            setIsDeviated(false);
        }

        setIsDeviationChecked(true);
        setTotal(bimWorkOrderPriceMTRDistributionTotalData?.bimWorkOrderPriceMTRDistributionTotal?.data?.total);
    }, [
        bimWorkOrderPriceMTRDistributionTotalData?.bimWorkOrderPriceMTRDistributionTotal?.data,
        handleOpenDeviatedConfirmationDialog,
    ]);

    useEffect(() => {
        setIsDeviationChecked(false);
    }, [mpValue, mzValue, opValue, ozValue]);

    return (
        <ModalDialog
            title="Редактирование стоимости"
            isOpen
            onClose={onModalClose}
            onConfirm={handleSubmit(onSubmitDialog)}
            isCommit={isCommit}
            isOnClickOutsideDisable
            style={{ width: 520 }}
        >
            <div className={cnMixSpace({ m: 'xl' })}>
                {isDeviated && (
                    <Informer
                        view="filled"
                        status="warning"
                        label="При перераспределении МТР изменилась исходная итоговая стоимость сметы"
                        className={cnMixSpace({ mB: 's' })}
                        icon={IconAlert}
                        size="s"
                    />
                )}
                <div style={headerStyle}>
                    <Text size="s" view="secondary" style={headerStyleLabel}>
                        Итоговая стоимость сметы
                    </Text>
                    <Text size="s">{formatNumberToRu(total, 0)}</Text>
                </div>
                <form>
                    <ControlNumberField
                        name="mp"
                        label="МП"
                        control={control}
                        labelPosition="left"
                        type="text"
                        view="default"
                        className={cnMixSpace({ mB: 's' })}
                        size="s"
                        style={styleWidth}
                    />
                    <ControlNumberField
                        name="op"
                        label="ОП"
                        control={control}
                        labelPosition="left"
                        type="text"
                        view="default"
                        className={cnMixSpace({ mB: 's' })}
                        size="s"
                        style={styleWidth}
                    />
                    <ControlNumberField
                        name="mz"
                        label="МЗ"
                        control={control}
                        labelPosition="left"
                        type="text"
                        view="default"
                        className={cnMixSpace({ mB: 's' })}
                        size="s"
                        style={styleWidth}
                    />
                    <ControlNumberField
                        name="oz"
                        label="ОЗ"
                        control={control}
                        labelPosition="left"
                        type="text"
                        view="default"
                        className={cnMixSpace({ mB: 's' })}
                        size="s"
                        style={styleWidth}
                    />
                </form>
            </div>
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
            <BimWorkOrderPriceMtrDistEditConfirmationDialog
                isOpened={isOpenDeviatedConfirmationDialog}
                onSubmit={handleActionDeviatedConfirmationQuery}
                onClose={onDeviatedConfirmationClose}
            />
        </ModalDialog>
    );
};

BimWorkOrderPriceMtrDistEdit.displayName = 'BimWorkOrderPriceMtrDistEdit';
